import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

export default function Offers() {
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState({});
  const [messages, setMessages] = useState({});

  useEffect(() => {
    api.get('/offers').then(({ data }) => setOffers(data.offers || [])).finally(() => setLoading(false));
  }, []);

  const handleClaim = async (id) => {
    if (!user) return alert('Please login to claim offers');
    setClaiming(c => ({ ...c, [id]: true }));
    try {
      await api.post(`/offers/${id}/claim`);
      setMessages(m => ({ ...m, [id]: { type: 'success', text: '✅ Offer claimed!' } }));
      setOffers(offers.map(o => o._id === id ? { ...o, claimedBy: [...o.claimedBy, { user: user.id }] } : o));
    } catch (err) {
      setMessages(m => ({ ...m, [id]: { type: 'error', text: err.response?.data?.message || 'Error' } }));
    } finally {
      setClaiming(c => ({ ...c, [id]: false }));
      setTimeout(() => setMessages(m => ({ ...m, [id]: null })), 3000);
    }
  };

  const isClaimed = (offer) => user && offer.claimedBy?.some(c => c.user === user.id || c.user?._id === user.id);

  return (
    <div style={{ paddingTop: 70 }}>
      <div className="page-header">
        <h1>DAILY OFFERS</h1>
        <p>Exclusive deals and bonuses — updated every day</p>
      </div>
      <div className="container" style={{ paddingBottom: 80 }}>
        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : offers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎁</div>
            <p style={{ fontSize: 18 }}>No offers available right now. Check back soon!</p>
          </div>
        ) : (
          <div className="grid-2">
            {offers.map(offer => {
              const claimed = isClaimed(offer);
              const expired = offer.validUntil && new Date(offer.validUntil) < new Date();
              const msg = messages[offer._id];
              return (
                <div key={offer._id} className="card fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 16, opacity: expired ? 0.6 : 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                        {offer.isDaily && <span className="badge badge-hot">Daily</span>}
                        {offer.platform?.name && <span style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-secondary)', padding: '2px 8px', borderRadius: 6 }}>{offer.platform.name}</span>}
                        {expired && <span className="badge" style={{ background: 'var(--text-muted)', color: '#fff' }}>Expired</span>}
                      </div>
                      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 8 }}>{offer.title}</h3>
                      <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.5 }}>{offer.description}</p>
                    </div>
                    {offer.discount && (
                      <div style={{ background: 'var(--gradient-gold)', padding: '12px 16px', borderRadius: 12, fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 22, color: '#fff', textAlign: 'center', flexShrink: 0, minWidth: 80 }}>{offer.discount}</div>
                    )}
                  </div>
                  {offer.code && (
                    <div style={{ background: 'var(--bg-secondary)', border: '1px dashed var(--accent-cyan)', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Promo Code:</span>
                      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent-cyan)', fontSize: 15, letterSpacing: '0.1em' }}>{offer.code}</span>
                    </div>
                  )}
                  {offer.validUntil && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>⏰ Valid until: {new Date(offer.validUntil).toLocaleDateString()}</div>
                  )}
                  {msg && (
                    <div style={{ background: msg.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${msg.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, borderRadius: 8, padding: '8px 12px', fontSize: 13, color: msg.type === 'success' ? '#10b981' : '#ef4444' }}>{msg.text}</div>
                  )}
                  <button
                    className={`btn ${claimed ? 'btn-secondary' : 'btn-primary'}`}
                    onClick={() => !claimed && !expired && handleClaim(offer._id)}
                    disabled={claimed || expired || claiming[offer._id]}
                    style={{ opacity: (claimed || expired) ? 0.6 : 1 }}
                  >
                    {claiming[offer._id] ? 'Claiming...' : claimed ? '✅ Claimed' : expired ? 'Expired' : '🎁 Claim Offer'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
